import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Send, MessageSquare, Loader2, Sparkles, Plus, Trash2 } from "lucide-react";

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); } catch { return ""; }
}

export default function Messenger() {
  const { isMaster, user } = useAuth();
  const [convs, setConvs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [simDialog, setSimDialog] = useState(false);
  const [simName, setSimName] = useState("");
  const [simText, setSimText] = useState("");
  const [rulesDialog, setRulesDialog] = useState(false);
  const [rules, setRules] = useState([]);
  const [newRule, setNewRule] = useState({ keywords: "", reply: "" });
  const listRef = useRef(null);
  const isAdmin = user?.role === "master" || user?.role === "admin";

  const loadConvs = async () => {
    try { const { data } = await api.get("/messenger/conversations"); setConvs(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openConv = async (c) => {
    setSelected(c);
    try {
      const { data } = await api.get(`/messenger/conversations/${c.id}/messages`);
      setMsgs(data);
      await loadConvs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const loadRules = async () => {
    try { const { data } = await api.get("/messenger/auto-replies"); setRules(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { loadConvs(); }, []);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [msgs.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !selected) return;
    setSending(true);
    try {
      await api.post(`/messenger/conversations/${selected.id}/send`, { text });
      setText("");
      await openConv(selected);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSending(false); }
  };

  const simulate = async () => {
    if (!simName.trim() || !simText.trim()) return toast.error("Nombre y texto requeridos");
    try {
      const { data } = await api.post("/messenger/simulate-inbound", { contact_name: simName, text: simText });
      toast.success(data.auto_reply ? "Consulta simulada + auto-respuesta enviada" : "Consulta simulada");
      setSimDialog(false); setSimName(""); setSimText("");
      await loadConvs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createRule = async () => {
    if (!newRule.keywords.trim() || !newRule.reply.trim()) return toast.error("Faltan datos");
    try {
      await api.post("/messenger/auto-replies", {
        keywords: newRule.keywords.split(",").map((k) => k.trim()).filter(Boolean),
        reply: newRule.reply, enabled: true,
      });
      toast.success("Regla creada");
      setNewRule({ keywords: "", reply: "" });
      await loadRules();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const deleteRule = async (id) => {
    try { await api.delete(`/messenger/auto-replies/${id}`); toast.success("Regla eliminada"); await loadRules(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="messenger-page">
      <div className="flex items-start gap-4 flex-wrap justify-between">
        <div>
          <div className="h-1 w-16 bg-brand-aqua mb-4 rounded-full" />
          <h1 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">Bandeja Messenger</h1>
          <p className="text-stone-600 mt-2 max-w-2xl"><span>Consultas entrantes de la Página de Facebook conectada. Auto-respuestas para FAQs.</span></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setSimDialog(true)} data-testid="messenger-simulate-btn"><Sparkles className="h-4 w-4 mr-2" />Simular consulta</Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => { loadRules(); setRulesDialog(true); }} data-testid="messenger-rules-btn">Auto-respuestas</Button>
          )}
        </div>
      </div>

      {/* Fase 24 · Guía de configuración Messenger cuando falta setup */}
      {isAdmin && convs.length === 0 && (
        <details className="card-elevated p-5 border-brand-gold/40 bg-brand-gold-soft/30" data-testid="messenger-setup-guide">
          <summary className="cursor-pointer font-serif-display text-lg text-brand-ink font-semibold flex items-center gap-2">
            <span className="inline-flex h-6 w-6 rounded-full bg-brand-gold text-brand-ink items-center justify-center text-xs font-bold">?</span>
            Requiere configuración Meta App + Page Access Token — clic para ver la guía
          </summary>
          <div className="mt-4 space-y-4 text-sm text-stone-700 leading-relaxed">
            <p><strong className="text-brand-ink">Paso 1 · Crear una App en Meta for Developers</strong></p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Ir a <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="underline text-brand-emerald-dark">developers.facebook.com/apps</a> y crear una app tipo <em>Business</em>.</li>
              <li>En <strong>Configuración → Básica</strong> copiar el <code className="bg-stone-100 px-1 rounded">App ID</code> y <code className="bg-stone-100 px-1 rounded">App Secret</code>.</li>
              <li>Pegarlos en <a href="/integraciones" className="underline text-brand-emerald-dark">Integraciones → Meta App</a> como <code>app_id</code> y <code>app_secret</code>.</li>
              <li>Elegir un <code>verify_token</code> aleatorio (cualquier cadena secreta) y guardarlo en la misma integración.</li>
            </ol>
            <p><strong className="text-brand-ink">Paso 2 · Obtener Page Access Token</strong></p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Ir a <a href="https://developers.facebook.com/tools/explorer/" target="_blank" rel="noreferrer" className="underline text-brand-emerald-dark">Graph API Explorer</a>, seleccionar la app y elegir la página.</li>
              <li>Permisos necesarios: <code>pages_manage_metadata</code>, <code>pages_messaging</code>, <code>pages_read_engagement</code>, <code>pages_show_list</code>.</li>
              <li>Copiar el <em>Page Access Token</em>. En <a href="/conectividad" className="underline text-brand-emerald-dark">Conectividad → Facebook Page</a> pegar el token + <code>page_id</code> de la página.</li>
            </ol>
            <p><strong className="text-brand-ink">Paso 3 · Suscribir el webhook</strong></p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>En la app de Meta → <strong>Messenger → Configuración → Webhooks</strong>.</li>
              <li>Callback URL: <code className="bg-stone-100 px-1 rounded">https://7-study.com/api/messenger/webhook</code></li>
              <li>Verify token: el mismo que guardaste en el paso 1.</li>
              <li>Suscribirse a los campos <code>messages</code>, <code>messaging_postbacks</code>.</li>
              <li>Luego "Add Subscriptions" a la página con esos mismos campos.</li>
            </ol>
            <p className="text-xs text-stone-500">
              Una vez completado, los mensajes entrantes aparecerán aquí automáticamente. La conexión por QR (WhatsApp Web) NO existe en la API oficial de Messenger.
            </p>
          </div>
        </details>
      )}

      <div className="card-elevated overflow-hidden grid grid-cols-1 md:grid-cols-3" style={{ minHeight: "540px" }}>
        {/* Lista conversaciones */}
        <div className="border-r border-border overflow-y-auto max-h-[540px]" data-testid="messenger-convs-list">
          {convs.map((c) => (
            <button
              key={c.id} onClick={() => openConv(c)}
              className={`w-full text-left p-4 border-b border-border hover:bg-stone-50 ${selected?.id === c.id ? "bg-brand-aqua-soft/50" : ""}`}
              data-testid={`messenger-conv-${c.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-brand-ink truncate">{c.contact_name}</span>
                {c.unread > 0 && <Badge className="bg-brand-aqua text-brand-ink border-brand-aqua-dark text-[10px] font-bold"><span>{c.unread}</span></Badge>}
              </div>
              <div className="text-xs text-stone-500 mt-1 line-clamp-1"><span>{c.last_message}</span></div>
              <div className="text-[10px] text-stone-400 mt-1"><span>{fmt(c.last_message_at)}</span></div>
            </button>
          ))}
          {convs.length === 0 && <div className="p-10 text-center text-sm text-stone-500">Sin conversaciones.</div>}
        </div>

        {/* Chat */}
        <div className="md:col-span-2 flex flex-col">
          {selected ? (
            <>
              <div className="p-4 border-b border-border flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-brand-aqua-soft flex items-center justify-center text-brand-aqua-dark font-bold">{selected.contact_name[0]}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-brand-ink"><span>{selected.contact_name}</span></div>
                  <div className="text-xs text-stone-500"><span>{selected.mode === "real" ? "Real (Meta)" : "Simulado"}</span></div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-stone-50/40" ref={listRef}>
                {msgs.map((m) => (
                  <div key={m.id} className={`max-w-md ${m.direction === "outbound" ? "ml-auto" : "mr-auto"}`}>
                    <div className={`rounded-2xl px-4 py-2 text-sm ${m.direction === "outbound" ? "bg-brand-emerald text-white rounded-br-sm" : "bg-white border border-border rounded-bl-sm"}`}>
                      <span>{m.text}</span>
                    </div>
                    <div className="text-[10px] text-stone-400 mt-1 px-2 flex items-center gap-2">
                      {m.auto_reply && <span className="text-brand-aqua-dark font-semibold">Auto</span>}
                      <span>{m.author_name}</span><span>·</span><span>{fmt(m.sent_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={send} className="border-t border-border p-3 flex gap-2 bg-white">
                <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Escriba una respuesta…" data-testid="messenger-input" />
                <Button type="submit" disabled={sending || !text.trim()} className="btn-primary" data-testid="messenger-send">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-stone-400"><MessageSquare className="h-8 w-8 mr-2" /><span>Seleccione una conversación</span></div>
          )}
        </div>
      </div>

      {/* Dialog simular */}
      <Dialog open={simDialog} onOpenChange={setSimDialog}>
        <DialogContent><DialogHeader><DialogTitle>Simular consulta entrante</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Nombre del contacto" value={simName} onChange={(e) => setSimName(e.target.value)} data-testid="sim-name" />
            <Textarea placeholder="Mensaje del contacto (prueba palabras clave: precio, ubicación, disponible)" value={simText} onChange={(e) => setSimText(e.target.value)} rows={3} data-testid="sim-text" />
            <Button onClick={simulate} className="btn-primary w-full" data-testid="sim-submit">Simular</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog reglas */}
      <Dialog open={rulesDialog} onOpenChange={setRulesDialog}>
        <DialogContent className="max-w-2xl"><DialogHeader><DialogTitle>Reglas de auto-respuesta</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 border border-dashed border-brand-emerald/30 p-3 rounded-lg">
              <Label className="text-xs">Palabras clave (separadas por coma)</Label>
              <Input value={newRule.keywords} onChange={(e) => setNewRule({ ...newRule, keywords: e.target.value })} placeholder="precio, costo, cuanto" data-testid="rule-keywords" />
              <Label className="text-xs">Respuesta automática</Label>
              <Textarea value={newRule.reply} onChange={(e) => setNewRule({ ...newRule, reply: e.target.value })} rows={2} data-testid="rule-reply" />
              <Button size="sm" onClick={createRule} className="btn-primary" data-testid="rule-create"><Plus className="h-4 w-4 mr-1" />Crear regla</Button>
            </div>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {rules.map((r) => (
                <div key={r.id} className="border border-border rounded-lg p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-stone-500 flex gap-1 flex-wrap">
                      {r.keywords.map((k) => <Badge key={k} className="bg-brand-aqua-soft text-brand-aqua-dark text-[10px]"><span>{k}</span></Badge>)}
                    </div>
                    <div className="text-sm mt-1"><span>{r.reply}</span></div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => deleteRule(r.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
